/**
 * "What MCOS sent" (/messages/log): what MCOS sent to patients, and whether it arrived.
 * The conversations inbox (S-13) is /messages.
 *
 * The client's sidebar has a Messages entry. This is the delivery log, not an
 * inbox, and the screen says so: MCOS sends, it does not receive. A screen
 * that looked like a two-way conversation would invite a patient reply that
 * nobody in the clinic would ever read, which is worse than not offering the
 * channel at all.
 *
 * The recipient column is stored already masked. A list of every patient email
 * address in the clinic is not something a page needs in order to answer the
 * question this page exists for, which is "did the plan reach her".
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from '../components/ui';

interface MessageRow {
  id: string;
  channel: string;
  trigger: string;
  recipient: string;
  status: string;
  error: string | null;
  createdAt: string;
  patient: { id: string; firstName: string; lastName: string } | null;
}

interface MessagePage {
  total: number;
  page: number;
  pageSize: number;
  messages: MessageRow[];
}

/** `checkin_month_3` reads as "Check-in, month 3". */
function triggerLabel(trigger: string): string {
  const checkin = /^checkin_month_(\d+)$/.exec(trigger);
  if (checkin) return `Check-in, month ${checkin[1]}`;
  const scan = /^body_scan_reminder_month_(\d+)$/.exec(trigger);
  if (scan) return `Body scan reminder, month ${scan[1]}`;
  const words = trigger.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'treatment_plan', label: 'Treatment plans' },
  { value: 'report_ready', label: 'Reports' },
];

export function MessagesPage() {
  const [page, setPage] = useState(1);
  const [trigger, setTrigger] = useState('');
  // One patient's messages, where the RETAIN screen's "Message Patient" lands.
  const [params, setParams] = useSearchParams();
  const patientId = params.get('patient');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['messages', page, trigger, patientId],
    queryFn: () =>
      api.get<MessagePage & { patient?: { id: string; firstName: string; lastName: string } | null }>(
        `/dashboard/messages?page=${page}&pageSize=25${trigger ? `&trigger=${trigger}` : ''}${patientId ? `&patient=${patientId}` : ''}`,
      ),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;

  const messages = data?.messages ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="What MCOS sent"
        subtitle={
          data?.patient
            ? `Everything MCOS has sent to ${data.patient.firstName} ${data.patient.lastName}. Texting patients stays off until it is switched on.`
            : 'Everything MCOS has sent to a patient: emails and texts, and whether they arrived. Conversations with patients are in Messages.'
        }
        actions={
          patientId ? (
            <button
              type="button"
              onClick={() => {
                setParams({}, { replace: true });
                setPage(1);
              }}
              className="text-[13px] font-semibold text-forest"
            >
              Show all patients
            </button>
          ) : undefined
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((filter) => (
            <button
              key={filter.value || 'all'}
              type="button"
              onClick={() => {
                setTrigger(filter.value);
                setPage(1);
              }}
              className={`rounded-control border px-3 py-1.5 text-xs font-semibold transition ${
                trigger === filter.value
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                  : 'border-ink/15 text-ink-muted hover:text-ink'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <div className="p-5">
            <LoadingRows rows={6} />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState
            title="Nothing sent yet"
            body="Treatment plans, report notices and monthly check-in reminders appear here once they go out."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>What was sent</Th>
                <Th>To</Th>
                <Th>When</Th>
                <Th align="right">Delivery</Th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr key={message.id} className="hover:bg-ink/[0.02]">
                  <Td className="max-sm:whitespace-nowrap">
                    {message.patient ? (
                      <Link
                        to={`/patients/${message.patient.id}`}
                        className="font-semibold text-ink hover:underline"
                      >
                        {message.patient.firstName} {message.patient.lastName}
                      </Link>
                    ) : (
                      <span className="text-ink-muted">-</span>
                    )}
                  </Td>
                  <Td className="text-ink max-sm:whitespace-nowrap">{triggerLabel(message.trigger)}</Td>
                  <Td className="text-ink-muted">
                    {message.recipient}
                    <span className="block text-xs">{message.channel.toLowerCase()}</span>
                  </Td>
                  <Td className="text-ink-muted max-sm:whitespace-nowrap">
                    {new Date(message.createdAt).toLocaleString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Td>
                  <Td align="right">
                    <Pill
                      className={
                        message.status === 'SENT'
                          ? 'bg-[var(--sage-soft)] text-sage-deep'
                          : 'bg-[var(--band-risk)]/10 text-[var(--band-risk)]'
                      }
                    >
                      {message.status.charAt(0) + message.status.slice(1).toLowerCase()}
                    </Pill>
                    {/* The failure reason, in full. A delivery log that hides
                        why something failed makes somebody open the server
                        logs to find out, and most people will simply resend. */}
                    {message.error && (
                      <span className="mt-1 block max-w-xs text-xs text-ink-muted">
                        {message.error}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
            className="rounded-control border border-ink/15 px-3 py-1.5 font-semibold text-ink disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-ink-muted">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((current) => current + 1)}
            className="rounded-control border border-ink/15 px-3 py-1.5 font-semibold text-ink disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
